import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchAllSpeakingExercises,
  updateSpeakingExercise,
  createSpeakingExercise,
  deleteSpeakingExercise,
  generateAllSpeakingAudio
} from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';
import AdminTable from './AdminTable';

const SpeakingExerciseManager = () => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioResult, setAudioResult] = useState(null);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const response = await fetchAllSpeakingExercises();
      setExercises(response.exercises || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching speaking exercises:', err);
      setError('Failed to load speaking exercises');
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExercises();
  }, []);

  const uniqueTopics = useMemo(() => {
    return [...new Set(exercises.map((item) => item.topic))].filter(Boolean);
  }, [exercises]);

  const handleSave = async (id, data) => {
    await updateSpeakingExercise(id, data);
    setExercises((prev) => prev.map((ex) => (ex.id === id ? { ...ex, ...data } : ex)));
  };

  const handleCreate = async (data) => {
    const newItem = await createSpeakingExercise(data);
    setExercises((prev) => [...prev, newItem]);
  };

  const handleDelete = async (id) => {
    await deleteSpeakingExercise(id);
    setExercises((prev) => prev.filter((ex) => ex.id !== id));
  };

  const handleGenerateAudio = async () => {
    if (!window.confirm('Tato akce vygeneruje audio pro všechny otázky. Existující audio bude přepsáno. Pokračovat?')) {
      return;
    }

    try {
      setGeneratingAudio(true);
      setAudioResult(null);
      const data = await generateAllSpeakingAudio();
      setAudioResult({
        success: true,
        message: `Audio vygenerováno pro ${data.generated_count} z ${data.total_exercises} otázek.`,
        errors: data.errors
      });
      // Refresh exercises to update has_audio status
      await fetchExercises();
    } catch (err) {
      console.error('Error generating audio:', err);
      setAudioResult({
        success: false,
        message: 'Nepodařilo se vygenerovat audio: ' + err.message
      });
    } finally {
      setGeneratingAudio(false);
    }
  };

  const columns = [
    { key: 'id', header: 'ID', width: 60 },
    { key: 'topic', header: 'Topic', editable: true, width: 120 },
    { key: 'question', header: 'Question', editable: true, width: 250 },
    { key: 'hint', header: 'Hint', editable: true, width: 250 },
    { key: 'instruction', header: 'Instruction', editable: true, width: 150 },
    { key: 'exercise_order', header: 'Order', editable: true, width: 70 },
    {
      key: 'has_audio',
      header: 'Audio',
      width: 60,
      render: (value) => (
        <span style={{ fontSize: '1.1rem' }} title={value ? 'Audio vygenerováno' : 'Audio chybí'}>
          {value ? '\u2705' : '\u274C'}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      width: 100,
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
  ];

  const editableFields = [
    { key: 'topic', type: 'text', label: 'Topic', required: true },
    { key: 'question', type: 'textarea', label: 'Question', minHeight: '60px', required: true },
    { key: 'hint', type: 'textarea', label: 'Hint', minHeight: '60px', required: true },
    { key: 'instruction', type: 'textarea', label: 'Instruction', minHeight: '40px' },
    { key: 'exercise_order', type: 'number', label: 'Order', default: 1 },
  ];

  if (loading && exercises.length === 0) {
    return <Loading />;
  }

  if (error && exercises.length === 0) {
    return <Error message={error} />;
  }

  const audioButton = (
    <button
      onClick={handleGenerateAudio}
      disabled={generatingAudio}
      style={{
        padding: '8px 16px',
        backgroundColor: generatingAudio ? '#6c757d' : '#17a2b8',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: generatingAudio ? 'not-allowed' : 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'background-color 0.2s ease'
      }}
      onMouseEnter={(e) => {
        if (!generatingAudio) {
          e.currentTarget.style.backgroundColor = '#138496';
        }
      }}
      onMouseLeave={(e) => {
        if (!generatingAudio) {
          e.currentTarget.style.backgroundColor = '#17a2b8';
        }
      }}
    >
      {generatingAudio ? (
        <>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>{'\u23F3'}</span>
          Generuji audio...
        </>
      ) : (
        <>
          Vygeneruj audio
        </>
      )}
    </button>
  );

  return (
    <div>
      {audioResult && (
        <div style={{
          marginBottom: '20px',
          padding: '12px 16px',
          borderRadius: '6px',
          backgroundColor: audioResult.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${audioResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          color: audioResult.success ? '#155724' : '#721c24'
        }}>
          <p style={{ margin: 0, fontWeight: '500' }}>{audioResult.message}</p>
          {audioResult.errors && audioResult.errors.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem' }}>
                Chyby ({audioResult.errors.length}):
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.85rem' }}>
                {audioResult.errors.slice(0, 5).map((err, index) => (
                  <li key={index}>
                    <strong>ID {err.exercise_id}</strong>: {err.error}
                  </li>
                ))}
                {audioResult.errors.length > 5 && (
                  <li>... a {audioResult.errors.length - 5} dalších chyb</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>

      <AdminTable
        data={exercises}
        columns={columns}
        onSave={handleSave}
        onCreate={handleCreate}
        onDelete={handleDelete}
        editableFields={editableFields}
        title="Speaking Exercises"
        filterField="topic"
        uniqueFilterValues={uniqueTopics}
        loading={loading}
        emptyMessage="No speaking exercises found."
        extraButtons={audioButton}
      />
    </div>
  );
};

export default SpeakingExerciseManager;
